import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

/** Operator (admin) sign-in. Magic-link only — gated server-side to a
 * known allowlist of admin emails. Replaces the legacy single-password
 * admin login, which is gone. */
export default function Login() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await api.requestAdminMagicLink(email);
      // Always show "check your inbox" — the API never tells us
      // whether the email is on the allowlist (avoids enumeration).
      setSent(true);
    } catch {
      // Even on a network error we show the same screen so timing
      // doesn't leak whether the email matched the allowlist.
      setSent(true);
    } finally { setBusy(false); }
  }

  return (
    <div className="h-full relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-run/10 blur-3xl" />
        <div className="absolute inset-0 grid-lines opacity-30" />
      </div>

      <div className="h-full grid place-items-center relative">
        <div className="w-[400px] animate-slide-up">
          <div className="flex flex-col items-center mb-8">
            <Wordmark className="h-7 mb-3" />
            <p className="text-xs text-dim flex items-center gap-2">
              <span className="text-warn">●</span>
              Operator access · admin email required
            </p>
          </div>

          {sent ? (
            <div className="card p-6">
              <div className="text-sm font-semibold text-ink mb-1">Check your inbox</div>
              <p className="text-xs text-dim leading-relaxed mb-4">
                If <span className="font-mono text-ink/80">{email}</span> is on
                the admin allowlist, a sign-in link is on its way. Valid for
                15 minutes.
              </p>
              <button
                onClick={() => setSent(false)}
                className="text-[11px] text-accent hover:underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="card p-6 space-y-4">
              <label className="block">
                <div className="text-[11px] uppercase tracking-wider text-dim mb-2">Admin email</div>
                <input
                  type="email" autoComplete="email" required autoFocus
                  value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@yourdomain.com"
                  className="input"
                />
              </label>
              <button disabled={busy || !email} className="btn-primary btn-lg w-full">
                {busy ? 'Sending…' : 'Email me an admin link'}
              </button>
              <p className="text-[11px] text-dim leading-relaxed">
                Only allowlisted emails receive a link. Everyone else gets a
                silent no-op — the endpoint never confirms whether your
                address is on the list.
              </p>
            </form>
          )}

          <p className="text-center text-[11px] text-dim mt-4">
            Not an admin?{' '}
            <Link to="/login" className="text-accent hover:underline">User sign-in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Sendepot wordmark — text + small accent square. Renders crisply at any size. */
export function Wordmark({ className = 'h-6' }: { className?: string }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <svg viewBox="0 0 24 24" className="h-full w-auto" fill="none" aria-hidden>
        <rect x="2" y="2" width="20" height="20" rx="5" fill="#8b5cf6" />
        <path d="M7 10 L12 7 L17 10 L17 14 L12 17 L7 14 Z" stroke="white" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
      <span className="font-semibold text-ink tracking-tight" style={{ fontSize: '1.05em' }}>
        Sendepot
      </span>
    </div>
  );
}
